import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  FiUsers,
  FiBriefcase,
  FiShoppingBag,
  FiCheckCircle,
  FiPower,
  FiTrash2,
  FiShield
} from 'react-icons/fi';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('businesses');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { showToast } = useToast();

  const fetchAdminData = useCallback(async () => {
    try {
      const [statsRes, usersRes, bizRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/users'),
        api.get('/admin/businesses')
      ]);

      if (statsRes.success) setStats(statsRes.data);
      if (usersRes.success) setUsers(usersRes.data);
      if (bizRes.success) setBusinesses(bizRes.data);
    } catch (err) {
      showToast(err.message || 'Failed to fetch admin data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  const handleToggleStatus = async (bizId) => {
    try {
      const res = await api.put(`/admin/business/${bizId}/toggle`);
      if (res.success) {
        showToast(res.message, 'success');
        fetchAdminData();
      }
    } catch (err) {
      showToast(err.message || 'Failed to toggle business status', 'error');
    }
  };

  const handleUpdatePlan = async (bizId, newPlan) => {
    try {
      const res = await api.put(`/admin/business/${bizId}/plan`, { plan: newPlan });
      if (res.success) {
        showToast(`Plan updated to ${newPlan}`, 'success');
        fetchAdminData();
      }
    } catch (err) {
      showToast(err.message || 'Failed to update plan', 'error');
    }
  };

  const handleDeleteBusiness = async () => {
    if (!deleteTarget) return;
    try {
      const res = await api.delete(`/admin/business/${deleteTarget._id}`);
      if (res.success) {
        showToast('Business deleted successfully', 'success');
        setDeleteTarget(null);
        fetchAdminData();
      }
    } catch (err) {
      showToast(err.message || 'Failed to delete business', 'error');
    }
  };

  if (loading) return <LoadingSpinner text="Loading Admin Control Panel..." />;

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto', color: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '2rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <FiShield style={{ color: 'var(--primary-500)' }} /> Platform Admin Panel
          </h1>
          <p style={{ color: '#94A3B8', fontSize: '0.95rem' }}>
            Manage platform users, business subscriptions, and statistics.
          </p>
        </div>
      </div>

      {/* STATS OVERVIEW */}
      <div className="stats-grid" style={{ marginBottom: '2rem' }}>
        <div className="stat-card">
          <div className="stat-icon-bg" style={{ background: 'rgba(99, 102, 241, 0.15)', color: '#6366F1' }}>
            <FiUsers />
          </div>
          <div>
            <div className="stat-val">{stats?.totalUsers || 0}</div>
            <div className="stat-lbl">Registered Users</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-bg" style={{ background: 'rgba(6, 182, 212, 0.15)', color: '#06B6D4' }}>
            <FiBriefcase />
          </div>
          <div>
            <div className="stat-val">{stats?.totalBusinesses || 0}</div>
            <div className="stat-lbl">Total Businesses</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-bg" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10B981' }}>
            <FiCheckCircle />
          </div>
          <div>
            <div className="stat-val">{stats?.activeBusinesses || 0}</div>
            <div className="stat-lbl">Active Online</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-bg" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#F59E0B' }}>
            <FiShoppingBag />
          </div>
          <div>
            <div className="stat-val">{stats?.totalProducts || 0}</div>
            <div className="stat-lbl">Total Products</div>
          </div>
        </div>
      </div>

      {/* TABS HEADER */}
      <div style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '1.5rem' }}>
        <button
          onClick={() => setActiveTab('businesses')}
          style={{
            padding: '0.75rem 1.25rem',
            fontWeight: 700,
            color: activeTab === 'businesses' ? 'var(--primary-500)' : '#94A3B8',
            borderBottom: activeTab === 'businesses' ? '3px solid var(--primary-500)' : 'none',
            fontSize: '1rem'
          }}
        >
          Manage Businesses ({businesses.length})
        </button>
        <button
          onClick={() => setActiveTab('users')}
          style={{
            padding: '0.75rem 1.25rem',
            fontWeight: 700,
            color: activeTab === 'users' ? 'var(--primary-500)' : '#94A3B8',
            borderBottom: activeTab === 'users' ? '3px solid var(--primary-500)' : 'none',
            fontSize: '1rem'
          }}
        >
          Manage Users ({users.length})
        </button>
      </div>

      {/* BUSINESSES TABLE */}
      {activeTab === 'businesses' && (
        <div className="glass-card" style={{ padding: '0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#111827', borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#94A3B8' }}>
                <th style={{ padding: '1rem 1.25rem' }}>Business Name</th>
                <th style={{ padding: '1rem 1.25rem' }}>Owner</th>
                <th style={{ padding: '1rem 1.25rem' }}>Slug URL</th>
                <th style={{ padding: '1rem 1.25rem' }}>Plan</th>
                <th style={{ padding: '1rem 1.25rem' }}>Status</th>
                <th style={{ padding: '1rem 1.25rem', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {businesses.map((b) => (
                <tr key={b._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <td style={{ padding: '1rem 1.25rem', fontWeight: 600 }}>{b.businessName}</td>
                  <td style={{ padding: '1rem 1.25rem', color: '#94A3B8' }}>
                    {b.userId?.name} ({b.userId?.email})
                  </td>
                  <td style={{ padding: '1rem 1.25rem', color: 'var(--secondary-500)' }}>/business/{b.slug}</td>
                  <td style={{ padding: '1rem 1.25rem' }}>
                    <select
                      className="form-control"
                      style={{ width: '110px', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                      value={b.plan}
                      onChange={(e) => handleUpdatePlan(b._id, e.target.value)}
                    >
                      <option value="FREE">FREE</option>
                      <option value="BASIC">BASIC</option>
                      <option value="PRO">PRO</option>
                    </select>
                  </td>
                  <td style={{ padding: '1rem 1.25rem' }}>
                    <span className={`badge ${b.isPublished ? 'badge-success' : 'badge-danger'}`}>
                      {b.isPublished ? 'Published' : 'Disabled'}
                    </span>
                  </td>
                  <td style={{ padding: '1rem 1.25rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleToggleStatus(b._id)}
                        className={`btn ${b.isPublished ? 'btn-secondary' : 'btn-primary'} btn-sm`}
                        title={b.isPublished ? 'Disable Business' : 'Enable Business'}
                      >
                        <FiPower /> {b.isPublished ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => setDeleteTarget(b)}
                        className="btn btn-danger btn-sm"
                        title="Delete Business"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* USERS TABLE */}
      {activeTab === 'users' && (
        <div className="glass-card" style={{ padding: '0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#111827', borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#94A3B8' }}>
                <th style={{ padding: '1rem 1.25rem' }}>User Name</th>
                <th style={{ padding: '1rem 1.25rem' }}>Email</th>
                <th style={{ padding: '1rem 1.25rem' }}>Role</th>
                <th style={{ padding: '1rem 1.25rem' }}>Associated Business</th>
                <th style={{ padding: '1rem 1.25rem' }}>Joined Date</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <td style={{ padding: '1rem 1.25rem', fontWeight: 600 }}>{u.name}</td>
                  <td style={{ padding: '1rem 1.25rem', color: '#94A3B8' }}>{u.email}</td>
                  <td style={{ padding: '1rem 1.25rem' }}>
                    <span className={`badge ${u.role === 'admin' ? 'badge-purple' : 'badge-info'}`}>
                      {u.role}
                    </span>
                  </td>
                  <td style={{ padding: '1rem 1.25rem' }}>
                    {u.business ? u.business.name : <em style={{ color: '#64748B' }}>No Business</em>}
                  </td>
                  <td style={{ padding: '1rem 1.25rem', color: '#94A3B8' }}>
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CONFIRM DELETE DIALOG */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Business Account"
        message={`Are you sure you want to delete "${deleteTarget?.businessName}" and all linked products/enquiries?`}
        onConfirm={handleDeleteBusiness}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default AdminDashboard;
